import { supabase } from '../lib/supabase';
import { auditService } from './auditService';

export interface SendQuoteEmailParams {
  to: string;
  subject: string;
  body: string;
  quoteId: string;
  cc?: string; // ADDED: Optional CC email address
  variables: {
    customerName: string;
    companyName: string;
    quoteReference: string;
    projectName: string;
    quoteTotal: string;
    validUntilDate: string;
    quoteLink: string;
    quoteBreakdown?: string; // ADDED: Optional quote breakdown HTML
  };
}

export interface SendEmailResponse {
  success: boolean;
  message?: string;
  error?: string;
}

class EmailService {
  /**
   * Replace template variables in email body
   */
  private replaceVariables(body: string, variables: SendQuoteEmailParams['variables']): string {
    let processedBody = body;
    
    // Replace all template variables with actual values
    processedBody = processedBody.replace(/{CUSTOMER_NAME}/g, variables.customerName);
    processedBody = processedBody.replace(/{COMPANY_NAME}/g, variables.companyName);
    processedBody = processedBody.replace(/{QUOTE_REFERENCE}/g, variables.quoteReference);
    processedBody = processedBody.replace(/{PROJECT_NAME}/g, variables.projectName);
    processedBody = processedBody.replace(/{QUOTE_TOTAL}/g, variables.quoteTotal);
    processedBody = processedBody.replace(/{VALID_UNTIL_DATE}/g, variables.validUntilDate);
    processedBody = processedBody.replace(/{QUOTE_LINK}/g, variables.quoteLink);
    processedBody = processedBody.replace(/{QUOTE_BREAKDOWN}/g, variables.quoteBreakdown || '');
    
    return processedBody;
  }

  /**
   * Convert plain text body to HTML with proper formatting
   */
  private convertToHtml(body: string): string {
    // Convert newlines to <br> tags
    let html = body.replace(/\n/g, '<br>');
    
    // Convert **text** to <strong>text</strong>
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Wrap in basic HTML structure with professional styling
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
              line-height: 1.6;
              color: #333;
              max-width: 600px;
              margin: 0 auto;
              padding: 20px;
            }
            .email-container {
              background: #ffffff;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              padding: 30px;
            }
            strong {
              color: #1a1a1a;
            }
            a {
              color: #0066cc;
              text-decoration: none;
            }
            a:hover {
              text-decoration: underline;
            }
          </style>
        </head>
        <body>
          <div class="email-container">
            ${html}
          </div>
        </body>
      </html>
    `;
  }

  /**
   * Send quote email via Supabase Edge Function using Resend
   */
  async sendQuoteEmail(params: SendQuoteEmailParams): Promise<SendEmailResponse> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('You must be logged in to send emails');
      }

      // Replace variables in body
      let processedBody = this.replaceVariables(params.body, params.variables);
      
      // Convert to HTML
      const htmlContent = this.convertToHtml(processedBody);

      // Call edge function with CC support - ALWAYS add tom.symoens@evoqia.eu as CC
      const { data, error } = await supabase.functions.invoke('send-quote-email', {
        body: {
          to: params.to,
          subject: params.subject,
          htmlBody: htmlContent,
          cc: params.cc || 'tom.symoens@evoqia.eu', // ADDED: Default CC to tom.symoens@evoqia.eu
        },
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Failed to send email');
      }

      // FIXED: Use actual quote UUID instead of reference code for audit logging
      try {
        await auditService.logEmailSend(params.quoteId, params.to, `Email sent: ${params.subject}`);
      } catch (auditError) {
        console.error('Failed to log email send activity:', auditError);
        // Don't fail the email send if audit logging fails
      }

      console.log('Email sent successfully:', data);
      
      return {
        success: true,
        message: data?.message || 'Email sent successfully',
      };
    } catch (error: any) {
      console.error('Error sending email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred',
      };
    }
  }

  /**
   * Generate default email template
   */
  getDefaultTemplate(variables: SendQuoteEmailParams['variables']): string {
    return `Dear {CUSTOMER_NAME},

We're pleased to provide you with our quote for your project.

**Quote Details:**
Quote Reference: {QUOTE_REFERENCE}
Project: {PROJECT_NAME}
Total Amount: {QUOTE_TOTAL}
Valid Until: {VALID_UNTIL_DATE}

Please review the complete quote details using this link:
{QUOTE_LINK}

If you have any questions, please don't hesitate to contact us.

Best regards,
Team Evoqia`;
  }

  /**
   * Generate HTML breakdown of quote items grouped by category
   */
  private generateQuoteBreakdownHtml(licenses: any, implementation: any): string {
    let html = '';
    
    // Helper function to group items by category
    const groupItemsByCategory = (items: any[]) => {
      const grouped: { [key: string]: any[] } = {};
      items.forEach(item => {
        const category = item.group || 'Overige';
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push(item);
      });
      return grouped;
    };

    // Format currency
    const formatCurrency = (amount: number) => {
      return `€${amount.toFixed(2).replace('.', ',')}`;
    };

    // Licenses Section
    if (licenses?.included && licenses?.items?.length > 0) {
      html += `
        <div style="margin-bottom: 25px;">
          <h4 style="color: #3b82f6; margin: 0 0 15px 0; font-size: 15px; font-weight: 600; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px;">
            📦 Software Licenties
          </h4>
      `;
      
      const licensesGrouped = groupItemsByCategory(licenses.items);
      
      Object.keys(licensesGrouped).forEach(category => {
        html += `
          <div style="margin-bottom: 15px;">
            <p style="color: #64748b; font-size: 13px; font-weight: 600; margin: 0 0 8px 0;">${category}</p>
            <table width="100%" cellpadding="6" cellspacing="0" style="font-size: 13px; border: 1px solid #e2e8f0; border-radius: 4px;">
              <thead>
                <tr style="background-color: #f8fafc;">
                  <th style="text-align: left; color: #64748b; font-weight: 600; padding: 8px; border-bottom: 1px solid #e2e8f0;">Product</th>
                  <th style="text-align: center; color: #64748b; font-weight: 600; padding: 8px; border-bottom: 1px solid #e2e8f0;">Aantal</th>
                  <th style="text-align: right; color: #64748b; font-weight: 600; padding: 8px; border-bottom: 1px solid #e2e8f0;">Per stuk</th>
                  <th style="text-align: right; color: #64748b; font-weight: 600; padding: 8px; border-bottom: 1px solid #e2e8f0;">Maandelijks</th>
                  <th style="text-align: right; color: #64748b; font-weight: 600; padding: 8px; border-bottom: 1px solid #e2e8f0;">Jaarlijks</th>
                </tr>
              </thead>
              <tbody>
        `;
        
        licensesGrouped[category].forEach((item, index) => {
          const bgColor = index % 2 === 0 ? '#ffffff' : '#f8fafc';
          html += `
                <tr style="background-color: ${bgColor};">
                  <td style="padding: 8px; color: #1e293b;">${item.productName}</td>
                  <td style="padding: 8px; text-align: center; color: #1e293b;">${item.quantity}</td>
                  <td style="padding: 8px; text-align: right; color: #1e293b;">${formatCurrency(item.unitPrice)}</td>
                  <td style="padding: 8px; text-align: right; color: #1e293b;">${formatCurrency(item.monthlyAmount)}</td>
                  <td style="padding: 8px; text-align: right; color: #1e293b; font-weight: 600;">${formatCurrency(item.annualAmount)}</td>
                </tr>
          `;
        });
        
        html += `
              </tbody>
            </table>
          </div>
        `;
      });
      
      html += `
          <div style="margin-top: 15px; padding: 10px; background-color: #f0f9ff; border-radius: 4px;">
            <table width="100%" cellpadding="4" cellspacing="0" style="font-size: 13px;">
              <tr>
                <td style="color: #0369a1; font-weight: 600;">Subtotaal Licenties (Maandelijks):</td>
                <td style="text-align: right; color: #0369a1; font-weight: 700;">${formatCurrency(licenses.monthlySubtotal)}</td>
              </tr>
              <tr>
                <td style="color: #0369a1; font-weight: 600;">Subtotaal Licenties (Jaarlijks):</td>
                <td style="text-align: right; color: #0369a1; font-weight: 700;">${formatCurrency(licenses.annualSubtotal)}</td>
              </tr>
            </table>
          </div>
        </div>
      `;
    }

    // Implementation Section
    if (implementation?.included && implementation?.items?.length > 0) {
      html += `
        <div style="margin-bottom: 25px;">
          <h4 style="color: #3b82f6; margin: 0 0 15px 0; font-size: 15px; font-weight: 600; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px;">
            🔧 Implementatie Services
          </h4>
      `;
      
      const implementationGrouped = groupItemsByCategory(implementation.items);
      
      Object.keys(implementationGrouped).forEach(category => {
        html += `
          <div style="margin-bottom: 15px;">
            <p style="color: #64748b; font-size: 13px; font-weight: 600; margin: 0 0 8px 0;">${category}</p>
            <table width="100%" cellpadding="6" cellspacing="0" style="font-size: 13px; border: 1px solid #e2e8f0; border-radius: 4px;">
              <thead>
                <tr style="background-color: #f8fafc;">
                  <th style="text-align: left; color: #64748b; font-weight: 600; padding: 8px; border-bottom: 1px solid #e2e8f0;">Service</th>
                  <th style="text-align: center; color: #64748b; font-weight: 600; padding: 8px; border-bottom: 1px solid #e2e8f0;">Uren</th>
                  <th style="text-align: right; color: #64748b; font-weight: 600; padding: 8px; border-bottom: 1px solid #e2e8f0;">Per uur</th>
                  <th style="text-align: right; color: #64748b; font-weight: 600; padding: 8px; border-bottom: 1px solid #e2e8f0;">Totaal</th>
                </tr>
              </thead>
              <tbody>
        `;
        
        implementationGrouped[category].forEach((item, index) => {
          const bgColor = index % 2 === 0 ? '#ffffff' : '#f8fafc';
          html += `
                <tr style="background-color: ${bgColor};">
                  <td style="padding: 8px; color: #1e293b;">${item.productName}</td>
                  <td style="padding: 8px; text-align: center; color: #1e293b;">${item.quantity}</td>
                  <td style="padding: 8px; text-align: right; color: #1e293b;">${formatCurrency(item.unitPrice)}</td>
                  <td style="padding: 8px; text-align: right; color: #1e293b; font-weight: 600;">${formatCurrency(item.annualAmount)}</td>
                </tr>
          `;
        });
        
        html += `
              </tbody>
            </table>
          </div>
        `;
      });
      
      html += `
          <div style="margin-top: 15px; padding: 10px; background-color: #f0f9ff; border-radius: 4px;">
            <table width="100%" cellpadding="4" cellspacing="0" style="font-size: 13px;">
              <tr>
                <td style="color: #0369a1; font-weight: 600;">Subtotaal Implementatie:</td>
                <td style="text-align: right; color: #0369a1; font-weight: 700;">${formatCurrency(implementation.annualSubtotal)}</td>
              </tr>
            </table>
          </div>
        </div>
      `;
    }

    // Empty state if no items
    if (html === '') {
      html = '<p style="color: #64748b; font-size: 14px; text-align: center; padding: 20px;">Geen items in deze offerte.</p>';
    }

    return html;
  }

  /**
   * Get quote acceptance confirmation email template (HTML format)
   * This template is loaded from the external template file
   */
  async getQuoteAcceptanceTemplate(variables: SendQuoteEmailParams['variables']): Promise<string> {
    try {
      // Fetch the HTML template from the public folder
      const response = await fetch('/email-templates/quote-acceptance-template.txt');
      if (!response.ok) {
        throw new Error('Failed to load email template');
      }
      
      let template = await response.text();
      
      // Remove the customization instructions section (everything after the --- line)
      const instructionsIndex = template.indexOf('---');
      if (instructionsIndex !== -1) {
        template = template.substring(0, instructionsIndex).trim();
      }
      
      return template;
    } catch (error) {
      console.error('Error loading email template:', error);
      // Fallback to basic template if file loading fails
      return `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Quote Accepted</title>
</head>
<body>
    <h1>Quote Accepted</h1>
    <p>A customer has accepted quote {QUOTE_REFERENCE}</p>
    <p><strong>Customer:</strong> {CUSTOMER_NAME}</p>
    <p><strong>Company:</strong> {COMPANY_NAME}</p>
    <p><strong>Project:</strong> {PROJECT_NAME}</p>
    <p><strong>Total:</strong> {QUOTE_TOTAL}</p>
    <p><a href="{QUOTE_LINK}">View Quote</a></p>
</body>
</html>`;
    }
  }

  /**
   * Send quote acceptance confirmation email to internal team
   */
  async sendQuoteAcceptanceEmail(params: {
    quoteId: string;
    customerName: string;
    companyName: string;
    quoteReference: string;
    projectName: string;
    quoteTotal: string;
    customerEmail: string;
    quoteLink: string;
    licenses?: any; // ADDED: License items data
    implementation?: any; // ADDED: Implementation items data
  }): Promise<SendEmailResponse> {
    try {
      console.log('🚀 Starting sendQuoteAcceptanceEmail with params:', {
        quoteId: params.quoteId,
        customerEmail: params.customerEmail,
        quoteReference: params.quoteReference,
      });

      // FIXED: Remove session check - allow unauthenticated customers to trigger emails
      // The Edge Function itself will handle authentication through the anon key

      // Generate quote breakdown HTML
      console.log('📊 Generating quote breakdown HTML...');
      const quoteBreakdownHtml = this.generateQuoteBreakdownHtml(
        params.licenses,
        params.implementation
      );
      console.log('✅ Quote breakdown HTML generated');

      // Email template variables
      const variables = {
        customerName: params.customerName,
        companyName: params.companyName,
        quoteReference: params.quoteReference,
        projectName: params.projectName,
        quoteTotal: params.quoteTotal,
        validUntilDate: '', // Not needed for acceptance email
        quoteLink: params.quoteLink,
        quoteBreakdown: quoteBreakdownHtml, // ADDED: Include breakdown HTML
      };

      // Get the HTML template from the external file
      console.log('📄 Loading HTML template...');
      const htmlTemplate = await this.getQuoteAcceptanceTemplate(variables);
      console.log('✅ HTML template loaded');
      
      // Replace variables in the HTML template
      console.log('🔄 Replacing variables in template...');
      const emailBody = this.replaceVariables(htmlTemplate, variables);
      console.log('✅ Variables replaced in template');

      // UPDATED: Send to customer with detailed HTML breakdown instead of simple text
      console.log('📧 Generating customer confirmation HTML...');
      const customerHtmlBody = this.generateCustomerConfirmationHtml(variables);
      console.log('✅ Customer confirmation HTML generated');
      
      console.log('📤 Sending customer confirmation email to:', params.customerEmail);
      const { data: customerData, error: customerError } = await supabase.functions.invoke('send-quote-email', {
        body: {
          to: params.customerEmail,
          subject: `Offerte ${params.quoteReference} - Bevestiging`,
          htmlBody: customerHtmlBody,
        },
      });

      if (customerError) {
        console.error('❌ Error sending customer email:', customerError);
        throw new Error(customerError.message || 'Failed to send customer confirmation email');
      }

      console.log('✅ Customer email sent successfully:', customerData);

      // Log customer email activity
      try {
        console.log('📝 Logging customer email activity...');
        await auditService.logEmailSend(params.quoteId, params.customerEmail, `Customer confirmation: Quote ${params.quoteReference} accepted`);
        console.log('✅ Customer email activity logged');
      } catch (auditError) {
        console.error('⚠️ Failed to log customer email activity:', auditError);
      }

      // FIXED: Remove session check for internal email - use direct Edge Function call
      // Send to internal team (tom.symoens@evoqia.eu) with professional HTML template
      console.log('📤 Sending internal notification email to: tom.symoens@evoqia.eu');
      const { data, error } = await supabase.functions.invoke('send-quote-email', {
        body: {
          to: 'tom.symoens@evoqia.eu',
          subject: `🎉 Quote Accepted: ${params.quoteReference}`,
          htmlBody: emailBody, // Send HTML directly without additional conversion
          cc: 'dirk.aerts@evoqia.eu',
        },
      });

      if (error) {
        console.error('❌ Edge function error (internal email):', error);
        throw new Error(error.message || 'Failed to send internal email');
      }

      console.log('✅ Internal email sent successfully:', data);

      // Log the email activity
      try {
        console.log('📝 Logging internal email activity...');
        await auditService.logEmailSend(params.quoteId, 'tom.symoens@evoqia.eu, dirk.aerts@evoqia.eu', `Internal notification: Quote ${params.quoteReference} accepted`);
        console.log('✅ Internal email activity logged');
      } catch (auditError) {
        console.error('⚠️ Failed to log internal email activity:', auditError);
      }

      console.log('🎉 All emails sent successfully!');
      return {
        success: true,
        message: 'Quote acceptance confirmation sent successfully to customer and internal team',
      };
    } catch (error: any) {
      console.error('💥 CRITICAL ERROR in sendQuoteAcceptanceEmail:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred',
      };
    }
  }

  /**
   * Generate customer confirmation HTML with detailed quote breakdown
   */
  private generateCustomerConfirmationHtml(variables: SendQuoteEmailParams['variables']): string {
    return `
<!DOCTYPE html>
<html lang="nl">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Offerte Bevestiging - Evoqia BV</title>
</head>
<body style="margin: 0; padding: 0; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background-color: #f4f4f4;">
    <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4; padding: 20px 0;">
        <tr>
            <td align="center">
                <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                    <!-- Main Content -->
                    <tr>
                        <td style="padding: 40px 30px;">
                            
                            <!-- Personal Greeting -->
                            <p style="color: #1e293b; font-size: 15px; line-height: 1.6; margin: 0 0 20px 0;">
                                Dear ${variables.customerName},
                            </p>
                            
                            <p style="color: #475569; font-size: 14px; line-height: 1.6; margin: 0 0 25px 0;">
                                Thank you for confirming our quote as per below: 
                            </p>
                            
                            <!-- Quote Breakdown Section -->
                            <div style="background-color: #ffffff; border: 1px solid #e2e8f0; border-radius: 6px; padding: 25px; margin-bottom: 25px;">
                                <h3 style="color: #1e293b; margin: 0 0 20px 0; font-size: 16px; font-weight: 600; border-bottom: 2px solid #3b82f6; padding-bottom: 10px;">Quote Details</h3>
                                
                                ${variables.quoteBreakdown}
                            </div>
                            

                            <!-- Contact Information -->
                            <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 20px; margin-bottom: 25px;">
                                <p style="color: #475569; margin: 0; font-size: 14px; line-height: 1.6;">
                                    You will be contacted asap regarding timing of execution.
                                </p>
                            </div>
                            
                            <!-- Closing -->
                            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e2e8f0;">
                                <p style="color: #475569; margin: 0 0 5px 0; font-size: 14px;">Thank you,</p>
                                <p style="color: #1e293b; margin: 0; font-size: 15px; font-weight: 600;">Team Evoqia</p>
                            </div>
                        </td>
                    </tr>
                    
                    <!-- Footer -->
                    <tr>
                        <td style="background-color: #1e293b; padding: 20px 30px; text-align: center; border-radius: 0 0 8px 8px;">
                            <p style="color: #94a3b8; margin: 0; font-size: 12px; line-height: 1.6;">
                                © 2026 Evoqia BV. Alle rechten voorbehouden.<br>
                                <a href="https://www.evoqia.eu" style="color: #60a5fa; text-decoration: none;">www.evoqia.eu</a>
                            </p>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>
    `;
  }
}

export const emailService = new EmailService();